import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, X, Mic, Send, Loader2, StopCircle, Volume2, VolumeX, ShieldAlert, Ear } from "lucide-react";
import { useApp, API } from "../context";
import { speak, fireResult, triggerCombo, triggerEffect, playEffectSound, isMuted, setMuted, isFxSoundOn, setFxSound, setVoiceConfig } from "../lib/founderFx";
import { runEffect } from "../lib/founderParticles";
import { personaMeta, personaGreeting } from "../lib/founderPersonas";
import { useFabsCollapsed } from "../lib/fabTidy";
import { useDraggableFab } from "../lib/useDraggableFab";
import { getWakeWords, getWakeEntries, matchWake, buildWakeRegex, WAKE_EVENT } from "../lib/wakeWords";

// Soft two-note chime so admins hear when listening starts ("on") and when the
// wake word is caught ("wake"). Uses WebAudio; silent if unavailable.
function playWakeChime(kind = "wake") {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = kind === "on" ? [587, 784] : [784, 1175];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      const t = now + i * 0.11;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.16, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.13);
      o.start(t); o.stop(t + 0.15);
    });
    setTimeout(() => { try { ctx.close(); } catch (_) { /* noop */ } }, 600);
  } catch (_) { /* noop */ }
}

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

// One-tap screen effects. Tapping fires the visual instantly — NO voice/text
// narration — and each effect sweeps the WHOLE screen (see founderParticles).
const EFFECT_CHIPS = [
  { e: "star-rain", label: "Star Rain", icon: "⭐" },
  { e: "bubbles", label: "Bubbles", icon: "🫧" },
  { e: "confetti", label: "Confetti", icon: "🎉" },
  { e: "hearts", label: "Hearts", icon: "💖" },
  { e: "fireworks", label: "Fireworks", icon: "🎆" },
  { e: "snow", label: "Snow", icon: "❄️" },
  { e: "rainbow-burst", label: "Rainbow", icon: "🌈" },
  { e: "star-explosion", label: "Star Blast", icon: "💥" },
];

// Two-in-one celebration combos — fire both effects together for a bigger moment.
const EFFECT_COMBOS = [
  { key: "stars-confetti", label: "Stars + Confetti", icon: "🎉", effects: ["star-rain", "confetti"] },
  { key: "rainbow-fireworks", label: "Rainbow + Fireworks", icon: "🎆", effects: ["rainbow-burst", "fireworks"] },
  { key: "hearts-bubbles", label: "Hearts + Bubbles", icon: "💖", effects: ["hearts", "bubbles"] },
];

// Floating admin command bar (Isaiah + admins only) available on every page,
// PLUS the global screen-effects overlay. Everything here also exists in the
// full Command Center page — same backend actions.
export default function FounderPowerBar({ enabled }) {
  const { token } = useApp();
  const fabsCollapsed = useFabsCollapsed();
  const ccDrag = useDraggableFab("iw_fab_pos_cc", useLocation().pathname.startsWith("/learn/tutor") ? 308 : 96);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState(null); // {kind, summary, input_text}
  const [recording, setRecording] = useState(false);
  const [muted, setMutedState] = useState(isMuted());
  const [fxSound, setFxSoundState] = useState(isFxSoundOn());
  const toggleFxSound = () => { const on = !fxSound; setFxSound(on); setFxSoundState(on); };
  const [customCombo, setCustomCombo] = useState([]);
  const toggleCustom = (e) => setCustomCombo((cur) => cur.includes(e) ? cur.filter((x) => x !== e) : (cur.length >= 2 ? [cur[1], e] : [...cur, e]));
  const [savedCombos, setSavedCombos] = useState(() => { try { return JSON.parse(localStorage.getItem("iw_saved_combos") || "[]"); } catch { return []; } });
  const persistSaved = (list) => { setSavedCombos(list); try { localStorage.setItem("iw_saved_combos", JSON.stringify(list)); } catch { /* quota */ } };
  const saveCurrentCombo = () => {
    if (customCombo.length !== 2) return;
    if (savedCombos.some((s) => s.effects.join() === customCombo.join())) { toast("That combo is already saved."); return; }
    const label = customCombo.map((e) => (EFFECT_CHIPS.find((c) => c.e === e)?.label) || e).join(" + ");
    persistSaved([...savedCombos, { effects: [...customCombo], label }].slice(-8));
    toast.success("Combo saved!");
  };
  const deleteSaved = (i) => persistSaved(savedCombos.filter((_, idx) => idx !== i));
  const [reality, setReality] = useState("normal");
  const [addr, setAddr] = useState("Boss");
  const [nick, setNick] = useState("");
  const [persona, setPersona] = useState("king_herald");
  const [fabHidden, setFabHidden] = useState(() => {
    try { return localStorage.getItem("iw_cc_fab_hidden") === "1"; } catch (_) { return false; }
  });
  // "Hey Command" wake word (Chrome SpeechRecognition; OFF by default). When on,
  // it listens for the word "command" and then opens the bar + starts a voice
  // command automatically — hands-free, like "Hey Siri".
  const SpeechRec = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
  const [wakeOn, setWakeOn] = useState(() => { try { return localStorage.getItem("iw_cc_wake") === "1"; } catch { return false; } });
  const wakeRef = useRef(null);
  const wantWakeRef = useRef(false);
  const startWakeRef = useRef(null);
  const wakeReRef = useRef(buildWakeRegex(getWakeWords()));
  const wakeEntriesRef = useRef(getWakeEntries());
  const wakeHandledRef = useRef(false);
  useEffect(() => {
    const h = (e) => {
      wakeEntriesRef.current = (e?.detail?.entries) || getWakeEntries();
      wakeReRef.current = buildWakeRegex((e?.detail?.words) || getWakeWords());
    };
    window.addEventListener(WAKE_EVENT, h);
    return () => window.removeEventListener(WAKE_EVENT, h);
  }, []);
  const [fxList, setFxList] = useState([]); // [{effect, key}] — supports concurrent combos
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const recStartRef = useRef(0);

  // Global effect overlay listener (works from the bar AND the Command Center page).
  useEffect(() => {
    const h = (e) => {
      const effect = e?.detail?.effect;
      if (!effect) return;
      const key = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      setFxList((cur) => [...cur, { effect, key }]);
      playEffectSound();
      setTimeout(() => setFxList((cur) => cur.filter((f) => f.key !== key)), 5400);
    };
    window.addEventListener("founder:effect", h);
    return () => window.removeEventListener("founder:effect", h);
  }, []);

  // Load current reality when the bar mounts / opens, and whenever cosmetics change.
  useEffect(() => {
    if (!enabled || !token) return undefined;
    setVoiceConfig({ token, api: API });
    const loadSettings = () => {
      axios.get(`${API}/founder/settings`, auth(token))
        .then((r) => {
          const st = r.data?.settings || {};
          setReality(st.reality || "normal");
          const nn = (st.active_nickname || "").trim();
          setAddr(nn || "Boss");
          setNick(nn);
          setPersona(st.active_persona || "king_herald");
          setVoiceConfig({ token, api: API, voice: st.active_voice || "shimmer" });
          // Auto-play any effects the admin chose to keep in Normal (once per app open).
          const pe = st.promoted_effects || [];
          if ((st.reality || "normal") === "normal" && pe.length) {
            let done = false;
            try { done = sessionStorage.getItem("iw_cc_autofx") === "1"; } catch (_) {}
            if (!done) {
              try { sessionStorage.setItem("iw_cc_autofx", "1"); } catch (_) {}
              triggerCombo(pe);
            }
          }
        })
        .catch(() => {});
    };
    loadSettings();
    window.addEventListener("founder:cosmetics-updated", loadSettings);
    return () => window.removeEventListener("founder:cosmetics-updated", loadSettings);
  }, [enabled, token, open]);

  const switchReality = async (mode) => {
    try {
      await axios.post(`${API}/founder/reality`, { mode }, auth(token));
      setReality(mode);
      window.dispatchEvent(new Event("founder:cosmetics-updated"));
      if (!muted) speak(mode === "command" ? `Welcome to your kingdom, ${addr}!` : `As you wish — back to your world, ${addr}.`);
      toast.success(mode === "command" ? "🌌 You're in your Universe!" : "↩️ Back to Normal");
    } catch (e) { toast.error("Couldn't switch."); }
  };

  const runResult = useCallback(async (data) => {
    setReply(data);
    if (data.kind === "gift") {
      try {
        const recips = data.recipients || (data.recipient ? [data.recipient] : []);
        const res = await axios.post(`${API}/founder/gift/voice-send`,
          { recipients: recips, ...(data.gift || {}) }, auth(token));
        const label = res.data?.payload?.label || "a gift";
        const sent = res.data?.sent_names || [];
        const skipped = res.data?.skipped || [];
        let msg = `Sent ${label} to ${sent.join(" & ")}! 🎁`;
        if (skipped.length) msg += ` — couldn't reach ${skipped.join(", ")} (no history yet)`;
        speak(`Sent ${label} to ${sent.join(" and ")}!`);
        toast.success(msg);
      } catch (e) {
        const msg = e?.response?.data?.detail || "Couldn't send that gift.";
        speak(msg);
        toast.error(msg);
      }
      return;
    }
    if (data.kind === "action" && data.action) {
      try {
        const res = await axios.post(`${API}/founder/command/execute`,
          { action: data.action, params: data.params }, auth(token));
        fireResult(data.action, res.data.command?.result);
        window.dispatchEvent(new Event("founder:cosmetics-updated"));
        // Pure screen effects "just happen" — no narration, no toast, and no
        // Universe/reality switch. Everything else stays relational + spoken.
        const isEffect = data.action === "play_effect" || data.action === "play_combo";
        if (!isEffect) {
          setReality("command"); // real commands drop you into your Universe
          speak(data.summary);
          toast.success(data.summary || "Done!");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "That command didn't work.");
      }
    } else {
      speak(data.summary);
    }
  }, [token]);

  const interpret = async (raw) => {
    const value = (raw ?? text).trim();
    if (!value) return;
    setBusy(true); setReply(null);
    try {
      const { data } = await axios.post(`${API}/founder/interpret`, { text: value }, auth(token));
      data.input_text = value;
      await runResult(data);
      setText("");
    } catch (e) {
      toast.error("I couldn't understand that.");
    } finally { setBusy(false); }
  };

  const askApproval = async () => {
    try {
      await axios.post(`${API}/founder/approval-request`, { request_text: reply?.input_text || text }, auth(token));
      toast.success("Sent to a grown-up! 🙌");
      speak("Sent to a grown-up for approval!");
      setReply(null);
    } catch (e) { toast.error("Couldn't send that."); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!blob.size || Date.now() - recStartRef.current < 600) { toast.error("Hold the mic and speak your command!"); return; }
        setBusy(true); setReply(null);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "command.webm");
          const { data } = await axios.post(`${API}/founder/voice`, fd, auth(token));
          data.input_text = data.transcript || "";
          await runResult(data);
        } catch (e) { toast.error("Couldn't understand the recording."); }
        finally { setBusy(false); if (wantWakeRef.current) setTimeout(() => { startWakeRef.current && startWakeRef.current(); }, 800); }
      };
      mediaRef.current = mr; mr.start(); recStartRef.current = Date.now(); setRecording(true);
    } catch (e) {
      toast.error("I need mic permission — you can also type your command!");
    }
  };
  const stopRec = () => { if (mediaRef.current && recording) { mediaRef.current.stop(); setRecording(false); } };

  const toggleMute = () => { const m = !muted; setMuted(m); setMutedState(m); if (!m) speak("Voice on!"); };

  // ── "Hey Command" wake word (best-effort, Chrome only) ───────────────────
  const stopWake = useCallback(() => {
    wantWakeRef.current = false;
    try { if (wakeRef.current) wakeRef.current.stop(); } catch (_) {}
    wakeRef.current = null;
  }, []);

  const startWake = useCallback(() => {
    if (!SpeechRec) return;
    wantWakeRef.current = true;
    if (wakeRef.current) return;
    try {
      const rec = new SpeechRec();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = "en-US";
      rec.onstart = () => { wakeHandledRef.current = false; };
      rec.onresult = (e) => {
        if (wakeHandledRef.current) return;
        let txt = "";
        for (let i = e.resultIndex; i < e.results.length; i++) txt += e.results[i][0].transcript;
        const hit = matchWake(txt, wakeEntriesRef.current);
        if (hit) {
          wakeHandledRef.current = true;
          playWakeChime("wake");
          try { rec.stop(); } catch (_) {}
          wakeRef.current = null;
          setOpen(true);
          if (!muted) speak(personaGreeting(persona, addr));
          const { entry, after } = hit;
          if (after && after.length >= 2) {
            // "hey command, what's the weather?" → run the whole thing at once.
            setTimeout(() => interpret(after), 250);
          } else if (entry.command) {
            // a bare mapped word (e.g. "party" → "million star blast").
            setTimeout(() => interpret(entry.command), 250);
          } else {
            // just the wake word → open the mic and listen for the command.
            setTimeout(() => { setRecording((r) => { if (!r) startRec(); return r; }); }, 450);
          }
        }
      };
      rec.onerror = () => {};
      rec.onend = () => {
        if (wakeRef.current === rec) wakeRef.current = null;
        if (wantWakeRef.current) setTimeout(() => { if (wantWakeRef.current && !wakeRef.current) startWake(); }, 500);
      };
      wakeRef.current = rec;
      rec.start();
    } catch (_) { /* mic busy / unsupported */ }
  }, [SpeechRec, muted, persona, addr]); // eslint-disable-line
  startWakeRef.current = startWake;

  const toggleWake = () => {
    const nv = !wakeOn;
    setWakeOn(nv);
    try { localStorage.setItem("iw_cc_wake", nv ? "1" : "0"); } catch (_) {}
    if (nv) { startWake(); playWakeChime("on"); toast.success(`Listening for "${getWakeWords().join('" / "')}" — say it to open your Command Center`); }
    else { stopWake(); toast("Wake word off"); }
  };

  useEffect(() => {
    if (enabled && wakeOn) startWake();
    return () => stopWake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, wakeOn]);

  if (!enabled) {
    // Still render the effects overlay so page-triggered effects show even if the
    // bar is hidden (it won't be, but safe).
    return fxList.length ? createPortal(
      <>{fxList.map((f) => <EffectsOverlay key={f.key} fx={f} name="" />)}</>,
      document.body,
    ) : null;
  }

  return createPortal(
    <>
      {fxList.map((f, i) => <EffectsOverlay key={f.key} fx={f} name={i === 0 ? nick : ""} />)}

      {open && (
        <div className="fixed bottom-24 right-4 z-[2147482000] w-[min(92vw,380px)] max-h-[70vh] overflow-y-auto overflow-x-hidden overscroll-contain rounded-3xl border-2 border-fuchsia-400/40 bg-[#0b1020]/95 backdrop-blur-xl shadow-2xl shadow-fuchsia-900/40 p-4" data-testid="power-bar-panel">
          <div className="flex items-center gap-2 mb-2 sticky -top-4 z-10 bg-[#0b1020]/95 backdrop-blur-xl py-2">
            <span className="text-lg">{personaMeta(persona).figure}</span>
            <p className="font-fredoka font-bold text-white text-sm flex-1">{personaMeta(persona).label} — your command?</p>
            {SpeechRec && (
              <button onClick={toggleWake} className={`w-8 h-8 rounded-lg flex items-center justify-center ${wakeOn ? "bg-fuchsia-500/30 ring-1 ring-fuchsia-300/60" : "bg-white/10"}`} title={wakeOn ? 'Wake word ON — say "Command"' : 'Turn on "Hey Command" wake word'} data-testid="power-bar-wake">
                <Ear size={16} className={wakeOn ? "text-fuchsia-200" : "text-white/60"} />
              </button>
            )}
            <button onClick={toggleMute} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center" title={muted ? "Voice off" : "Voice on"} data-testid="power-bar-mute">
              {muted ? <VolumeX size={16} className="text-white/60" /> : <Volume2 size={16} className="text-fuchsia-200" />}
            </button>
            <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center" data-testid="power-bar-close">
              <X size={16} className="text-white/70" />
            </button>
          </div>

          {/* Reality switch */}
          <div className="flex items-center gap-1 mb-2 p-1 rounded-2xl bg-white/5 border border-white/10" data-testid="power-bar-reality">
            <button onClick={() => switchReality("normal")}
              className={`flex-1 py-1.5 rounded-xl text-xs font-bold transition-all ${reality === "normal" ? "bg-white/15 text-white" : "text-white/50"}`}
              data-testid="power-bar-reality-normal">Normal</button>
            <button onClick={() => switchReality("command")}
              className={`flex-1 py-1.5 rounded-xl text-xs font-bold transition-all ${reality === "command" ? "bg-gradient-to-r from-fuchsia-500 to-amber-400 text-white" : "text-white/50"}`}
              data-testid="power-bar-reality-command">🌌 My Universe</button>
          </div>

          <div className="flex items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !busy) interpret(); }}
              placeholder="Say a command, or ask me anything…"
              className="flex-1 min-w-0 bg-slate-800/80 border border-white/15 rounded-2xl px-3 py-2.5 text-sm text-white placeholder-white/35 focus:border-fuchsia-500/60 focus:outline-none"
              data-testid="power-bar-input"
              disabled={busy || recording}
            />
            <button onClick={recording ? stopRec : startRec} disabled={busy}
              className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${recording ? "bg-rose-500 animate-pulse" : "bg-white/10"}`}
              data-testid="power-bar-mic">
              {recording ? <StopCircle size={18} className="text-white" /> : <Mic size={17} className="text-fuchsia-200" />}
            </button>
            <button onClick={() => interpret()} disabled={busy || recording || !text.trim()}
              className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-amber-400 flex items-center justify-center disabled:opacity-40"
              data-testid="power-bar-send">
              {busy ? <Loader2 size={17} className="animate-spin text-white" /> : <Send size={16} className="text-white" />}
            </button>
          </div>

          {/* One-tap screen effects — fire instantly, no narration */}
          <div className="mt-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">Screen effects</p>
              <button onClick={toggleFxSound}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border transition-all ${fxSound ? "bg-white/5 border-white/10 text-white/60" : "bg-white/5 border-white/10 text-white/35"}`}
                title={fxSound ? "Effect sound on — tap for calm/silent effects" : "Effect sound off (calm)"}
                data-testid="power-bar-fx-sound-toggle">
                {fxSound ? <Volume2 size={12} /> : <VolumeX size={12} />}
                {fxSound ? "Sound" : "Calm"}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {EFFECT_CHIPS.map((f) => (
                <button key={f.e} onClick={() => triggerEffect(f.e)} disabled={recording}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/75 hover:bg-white/10 active:scale-95 transition-transform disabled:opacity-40"
                  data-testid={`power-bar-fx-${f.e}`}>
                  <span className="mr-1">{f.icon}</span>{f.label}
                </button>
              ))}
            </div>
            {/* Two-in-one celebration combos */}
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider mt-2.5 mb-1.5">Combos</p>
            <div className="flex flex-wrap gap-1.5">
              {EFFECT_COMBOS.map((c) => (
                <button key={c.key} onClick={() => triggerCombo(c.effects)} disabled={recording}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-fuchsia-500/10 border border-fuchsia-400/25 text-fuchsia-100 hover:bg-fuchsia-500/20 active:scale-95 transition-transform disabled:opacity-40"
                  data-testid={`power-bar-combo-${c.key}`}>
                  <span className="mr-1">{c.icon}</span>{c.label}
                </button>
              ))}
            </div>
            {/* Build-your-own combo — pick any two effects */}
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider mt-2.5 mb-1.5">Build your own (pick 2)</p>
            <div className="flex flex-wrap gap-1.5">
              {EFFECT_CHIPS.map((f) => {
                const sel = customCombo.includes(f.e);
                return (
                  <button key={f.e} onClick={() => toggleCustom(f.e)}
                    className={`text-[11px] font-bold px-2.5 py-1 rounded-full border active:scale-95 transition-transform ${sel ? "bg-amber-400 text-slate-900 border-amber-300" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}
                    data-testid={`power-bar-builder-${f.e}`}>
                    <span className="mr-1">{f.icon}</span>{f.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex gap-1.5">
              <button onClick={() => { if (customCombo.length === 2) triggerCombo(customCombo); }}
                disabled={customCombo.length !== 2}
                className="flex-1 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-amber-400 text-white font-bold text-xs disabled:opacity-40 active:scale-95 transition-transform"
                data-testid="power-bar-builder-fire">
                {customCombo.length === 2 ? "▶ Play my combo" : `Pick ${2 - customCombo.length} more`}
              </button>
              <button onClick={saveCurrentCombo} disabled={customCombo.length !== 2}
                className="px-3 py-2 rounded-xl bg-white/10 border border-white/15 text-white/80 font-bold text-xs disabled:opacity-40 active:scale-95 transition-transform"
                data-testid="power-bar-builder-save">
                ♥ Save
              </button>
            </div>
            {savedCombos.length > 0 && (
              <>
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider mt-2.5 mb-1.5">Saved combos</p>
                <div className="flex flex-wrap gap-1.5">
                  {savedCombos.map((s, i) => (
                    <span key={`${s.effects.join()}-${i}`} className="inline-flex items-center rounded-full bg-emerald-500/10 border border-emerald-400/25 overflow-hidden">
                      <button onClick={() => triggerCombo(s.effects)}
                        className="text-[11px] font-bold pl-2.5 pr-1.5 py-1 text-emerald-100 hover:bg-emerald-500/20 active:scale-95 transition-transform"
                        data-testid={`power-bar-saved-${i}`}>
                        {s.label}
                      </button>
                      <button onClick={() => deleteSaved(i)} className="px-1.5 py-1 text-emerald-200/60 hover:text-rose-300" data-testid={`power-bar-saved-del-${i}`} aria-label="Delete saved combo">
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {reply && (
            <div className="mt-2.5 rounded-2xl bg-white/5 border border-white/10 p-2.5" data-testid="power-bar-reply">
              <p className="text-white/85 text-sm font-nunito">
                {reply.kind === "needs_approval" && <ShieldAlert size={13} className="inline text-amber-300 mr-1" />}
                {reply.summary}
              </p>
              {reply.kind === "needs_approval" && (
                <button onClick={askApproval} className="mt-2 w-full py-2 rounded-xl bg-amber-400 text-slate-900 font-bold text-sm" data-testid="power-bar-approval">
                  Ask a grown-up
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Floating trigger (with an X to tuck it away, and a peek handle to bring it back) */}
      {!fabsCollapsed && (fabHidden ? (
        <button
          onClick={() => { setFabHidden(false); try { localStorage.setItem("iw_cc_fab_hidden", "0"); } catch (_) {} }}
          className="fixed bottom-[84px] md:bottom-5 right-0 z-[2147482000] flex items-center justify-center active:scale-95 transition-transform"
          style={{ width: 26, height: 46, borderTopLeftRadius: 9999, borderBottomLeftRadius: 9999,
            border: "1px solid rgba(217,70,239,0.5)", borderRight: "none",
            background: "linear-gradient(135deg,#d946ef,#f59e0b)", boxShadow: "0 4px 14px rgba(0,0,0,0.4)" }}
          title="Show command bar" data-testid="power-bar-restore" aria-label="Show command bar">
          <Zap size={15} className="text-white" fill="currentColor" />
        </button>
      ) : (
        <div className="fixed z-[2147482000]" style={ccDrag.style} {...ccDrag.bind}>
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); setFabHidden(true); try { localStorage.setItem("iw_cc_fab_hidden", "1"); } catch (_) {} }}
            className="absolute -top-1.5 -left-1.5 z-10 w-4 h-4 rounded-full bg-slate-900 border border-white/60 flex items-center justify-center shadow hover:bg-black hover:scale-110 transition-all"
            title="Hide (tap the tab on the edge to bring it back)" data-testid="power-bar-hide" aria-label="Hide command bar">
            <X size={10} className="text-white/90" />
          </button>
          <button onClick={() => { if (ccDrag.shouldSwallowClick()) return; const n = !open; setOpen(n); if (n && !muted) speak(personaGreeting(persona, addr)); }}
            className={`w-11 h-11 rounded-full bg-gradient-to-br from-fuchsia-500 via-purple-500 to-amber-400 shadow-2xl shadow-fuchsia-900/50 flex items-center justify-center border-2 active:scale-95 transition-transform ${reality === "command" ? "border-emerald-300 ring-2 ring-emerald-300/60" : "border-white/20"}`}
            title="Isaiah's Command Center — drag to move" data-testid="power-bar-fab">
            <Zap size={22} className="text-white" fill="currentColor" />
            {reality === "command" && <span className="absolute -top-1 -right-1 text-sm" data-testid="power-bar-fab-universe">🌌</span>}
          </button>
        </div>
      ))}
    </>,
    document.body,
  );
}

function EffectsOverlay({ fx, name }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const stop = runEffect(ref.current, fx.effect, 4200);
    return () => { try { stop(); } catch (_) {} };
  }, [fx.key, fx.effect]);
  return (
    <div className="fixed inset-0 pointer-events-none z-[2147483000]" data-testid="founder-effects-layer">
      <canvas
        ref={ref}
        className="absolute inset-0"
        style={{ width: "100vw", height: "100vh" }}
        data-testid="founder-effects-overlay"
        data-effect={fx.effect}
      />
      {name && (
        <div className="absolute inset-0 flex items-center justify-center" data-testid="founder-effects-name">
          <p key={fx.key} className="cc-name-burst font-fredoka font-black text-center px-4 text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-fuchsia-400 to-cyan-300"
            style={{ fontSize: "clamp(2rem, 9vw, 5rem)", WebkitTextStroke: "1px rgba(255,255,255,0.15)", textShadow: "0 4px 30px rgba(0,0,0,0.35)" }}>
            GO {name.toUpperCase()}!
          </p>
        </div>
      )}
    </div>
  );
}
